<template>
  <div class="relative">
    <header class="report-header relative">
      <div v-if="error" class="error text-center">
        {{ error }}
      </div>
      <h1 class="page-header text-center text-3xl">user permission report</h1>
      <div class="text-center">
        analyze the permission level of your users
      </div>
      <div class="download-wrapper flex justify-center items-center absolute top-2 right-5">
        <div v-if="downloadReportLoading" class="flex justify-center items-center w-40 mt-3">
          <loading-icon />
        </div>
        <template v-else>
          <a v-if="downloadUrl" :href="downloadUrl" class="button" download>
            download report
          </a>
          <button
            v-else
            :class="{ disabled: !reportId }"
            class="button"
            @click="generateExport"
          >
            download report
          </button>
        </template>
      </div>
      <input
        class="searchbox mt-12"
        type="text"
        placeholder="search"
        maxlength="100"
        :disabled="!reportId"
        v-model="searchText"
        @input="getSearchResults"
      >
    </header>

    <div v-if="loadingReport" class="flex justify-center align-center mt-12">
      <loading-icon />
    </div>
    <div v-else>
      <sb-custom-table class="sb-table pr-20" :rows="tableRows" :columns="tableColumns" disableTableSort/>
      <div v-if="reportLoaded && tableRows.length < 1" class="no-results-message text-center mt-32">
        no users found
      </div>
      <div v-if="pagesTotal > 1" class="pagination-wrapper flex justify-center items-center mt-10">
        <pagination
          :page.sync="page"
          :pages="pagesTotal"
          :gap="2"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import { SbCustomTable, Pagination, LoadingIcon } from '@springbig/sb-components';
import { ReportAPI } from '../../services/api/Reports';

export default {
  name: "UserPermissionsReport",
  components: {
    SbCustomTable,
    Pagination,
    LoadingIcon,
  },
  data() {
    return {
      downloadUrl: "",
      page: 0,
      pagesTotal: 1,
      reportId: null,
      tableRows: [],
      reportLoaded: false,
      loadingReport: false,
      downloadReportLoading: false,
      error: null,
      tableColumns: [],
      searchText: null,
    }
  },
  created() {
    this.runReport();
    this.getSearchResults = debounce(this.getSearchResults, 500);
  },
  watch: {
    async page(value) {
      this.loadingReport = true;
      await this.fetchReportData(value + 1);
      this.loadingReport = false;
    }
  },
  methods: {
    handleError(error) {
      this.error = error;
    },
    async runReport() {
      this.loadingReport = true;
      await this.generateReport();
      await this.fetchReportData(1);
      this.loadingReport = false;
    },
    async fetchReportData(page) {
      try {
        const { data } = await ReportAPI.getReportById(this.reportId, page, { searchText: this.searchText });
        const { results } = data.report.output_data;

        if (!results) return this.handleError('error, please refresh or contact support');

        this.pagesTotal = data.pagination.total_pages;
        this.tableRows = results.map(result => ({ ...result }));

        if (results.length > 0) {
          const columns = Object.keys(results[0]).map(key => ({
            key,
            value: key.replaceAll('_', ' '),
          }));
          
          this.tableColumns = columns;
        }

        this.reportLoaded = true;
      } catch(error) {
        this.handleError(error);
      }
    },
    async generateReport() {
      this.page = 0;
      try {
        const { data } = await ReportAPI.createReport('Reports::UserPermissionsReport');
        this.reportId = data.report?.id;
      } catch (error) {
        this.handleError(error);
      }
    },
    async checkDownloadUrlStatus() {
      try {
        const { data } = await ReportAPI.getReportDownloadUrl(this.reportId);
        if (data.report.download_url) {
          this.downloadReportLoading = false;
          this.downloadUrl = data.report.download_url;

          // Download file right after generation is done
          const link = document.createElement('a');
          link.href = this.downloadUrl;
          link.download = true;
          link.click();
          link.remove();
        }
        else if(data.report.export_state === 'failed') {
          this.handleError(new Error('report generation failed. please try again.'));
          this.downloadReportLoading = false;
        }
        else {
          setTimeout(this.checkDownloadUrlStatus, 1000);
        }
      }
      catch(error) {
        this.handleError(error);
        this.downloadReportLoading = false;
      }
    },
    generateExport() {
      if (!this.downloadUrl) {
        this.downloadReportLoading = true;
        ReportAPI.generateExportByReportId(this.reportId);
        setTimeout(this.checkDownloadUrlStatus, 1000);
      }
    },
    getSearchResults() {
      this.fetchReportData(1);
    }
  }
};
</script>

<style lang="scss" scoped>
  .report-header, .pagination-wrapper {
    // header & pagination should not overflow past the right screen edge like the table
    // so limit width to: screen width - .left-menu width - #content left & right padding
    max-width: calc(100vw - 270px - 6rem);
  }
  @media only screen and (max-width: 1023px) {
    .report-header, .pagination-wrapper {
      min-width: calc(100vw - 2rem);
    }
    .download-wrapper {
      position: static;
    }
  }
  .page-header {
    margin: 0;
  }
  .button {
    display: block;
    margin: 10px auto 0 auto;
    height: 30px;
    font-size: 1rem;
    padding: 5px 20px;
    color: rgba(255, 255, 255, 0.7);
    border-radius: 5px;
  }
  .searchbox {
    max-width: 530px;
    &:active, &:focus {
      border-color: #128ff9;
    }
    &::placeholder {
      color: rgba(74, 74, 74, 0.5);
    }
  }
  .no-results-message {
    color: rgba(74, 74, 74, 0.5);
  }
  .sb-table::v-deep {
    width: fit-content;
  }
</style>
